/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "../Stock/types";
import { toBaseQty } from "../Stock/units";
import type { EditableReceiptLine, ImportedReceipt, Receipt } from "./types";
import { isHeld, ReceiptLineRow } from "./ReceiptLineRow";

type Supplier = { id: number; name: string };

type Props = {
  /** Existing receipt to open, or null for a new one. */
  receiptId: number | null;
  /** A new receipt pre-filled from an imported e-invoice. */
  initial?: ImportedReceipt | null;
  onClose: (changed: boolean) => void;
};

const emptyLine = (): EditableReceiptLine => ({
  supplier_item_id: null,
  stock_item_id: null,
  description: "",
  supplier_code: null,
  gtin: null,
  quantity: 0,
  unit: "",
  unit_price: 0,
  price_confirmed: false,
});

const today = () => new Date().toISOString().slice(0, 10);

// Enter what arrived (a delivery or an invoice), save it as a draft, then
// post it to add the stock. See docs/plans/inventory_and_costing/05_purchasing.md.
export const ReceiptEditor = defineComponent<Props>({
  name: "ReceiptEditor",
  props: ["receiptId", "initial", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const busy = ref(false);
    const changed = ref(false);
    const id = ref<number | null>(props.receiptId);
    const status = ref<"draft" | "posted">("draft");
    const supplierId = ref<number | null>(null);
    const reference = ref("");
    const receivedOn = ref(today());
    const notes = ref("");
    const documentKey = ref<string | null>(null);
    const lines = ref<EditableReceiptLine[]>([emptyLine()]);
    const stockItems = ref<StockItem[]>([]);
    const supplierList = ref<Supplier[]>([]);

    const readOnly = computed(() => status.value === "posted");
    const imported = ref<{ total: number; unmatched: number; scan: boolean } | null>(null);

    const applyImport = (draft: ImportedReceipt) => {
      supplierId.value = draft.supplier_id;
      reference.value = draft.reference ?? "";
      documentKey.value = draft.document_key ?? null;
      if (draft.received_at) receivedOn.value = draft.received_at.slice(0, 10);
      lines.value = draft.lines.map((l) => ({
        supplier_item_id: l.supplier_item_id,
        stock_item_id: l.stock_item_id,
        description: l.description,
        supplier_code: l.supplier_code,
        gtin: l.gtin,
        quantity: l.quantity,
        unit: l.unit ?? "",
        unit_price: Number(l.unit_price.toFixed(4)),
        price_confirmed: false,
      }));
      imported.value = {
        total: draft.lines.length,
        unmatched: draft.lines.filter((l) => !l.stock_item_id).length,
        scan: draft.source === "scan",
      };
    };

    const applyReceipt = (r: Receipt) => {
      id.value = r.id;
      status.value = r.status;
      supplierId.value = r.supplier_id;
      reference.value = r.reference ?? "";
      receivedOn.value = r.received_at.slice(0, 10);
      notes.value = r.notes ?? "";
      documentKey.value = r.document_key;
      lines.value = r.lines.map((l) => ({
        supplier_item_id: l.supplier_item_id,
        stock_item_id: l.stock_item_id,
        description: l.description ?? "",
        supplier_code: l.supplier_code,
        gtin: l.gtin,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
        price_confirmed: l.price_confirmed,
      }));
    };

    onMounted(async () => {
      try {
        const [items, sups, receipt] = await Promise.all([
          trpc.stock.list.query(),
          trpc.purchasing.suppliers.query(),
          props.receiptId ? trpc.purchasing.receipt.query({ id: props.receiptId }) : null,
        ]);
        stockItems.value = (items as StockItem[]).filter((s) => s.active);
        supplierList.value = sups.filter((s) => s.active);
        if (receipt) applyReceipt(receipt as Receipt);
        else if (props.initial) applyImport(props.initial);
      } catch {
        toast.showToast(t("inventory.purchases.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    const itemFor = (line: EditableReceiptLine) =>
      stockItems.value.find((s) => s.id === line.stock_item_id);

    const filledLines = computed(() =>
      lines.value.filter((l) => l.stock_item_id && l.quantity > 0),
    );
    const total = computed(() =>
      filledLines.value.reduce((sum, l) => sum + l.quantity * l.unit_price, 0),
    );
    const invalid = computed(() =>
      filledLines.value.some((l) => {
        const item = itemFor(l);
        return !item || toBaseQty(item, 1, l.unit) == null || l.unit_price < 0;
      }),
    );
    const held = computed(() => filledLines.value.some((l) => isHeld(l, itemFor(l))));
    // Lines with content but no stock item would be dropped on save (an
    // imported invoice line nobody has mapped yet): block until mapped or
    // removed.
    const unmapped = computed(
      () => lines.value.filter((l) => !l.stock_item_id && (l.description || l.quantity > 0)).length,
    );

    const save = async () => {
      const saved = await trpc.purchasing.saveReceipt.mutate({
        id: id.value ?? undefined,
        supplier_id: supplierId.value,
        reference: reference.value || null,
        // Today means now (so same-day deliveries keep their order); an
        // earlier date is stored at noon to stay on that day in any timezone.
        received_at:
          receivedOn.value === today()
            ? new Date().toISOString()
            : new Date(`${receivedOn.value}T12:00:00`).toISOString(),
        notes: notes.value || null,
        document_key: documentKey.value,
        lines: filledLines.value.map((l) => ({
          supplier_item_id: l.supplier_item_id,
          stock_item_id: l.stock_item_id!,
          description: l.description || null,
          supplier_code: l.supplier_code,
          gtin: l.gtin,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          price_confirmed: l.price_confirmed,
        })),
      });
      changed.value = true;
      applyReceipt(saved as Receipt);
      return saved as Receipt;
    };

    const run = async (action: () => Promise<void>, failKey: string) => {
      busy.value = true;
      try {
        await action();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t(failKey), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const handleSave = () =>
      run(async () => {
        await save();
        toast.showToast(t("inventory.purchases.saved"), { type: "success" });
      }, "inventory.purchases.save_failed");

    const handlePost = () =>
      run(async () => {
        if (!confirm(t("inventory.purchases.post_confirm"))) return;
        const saved = await save();
        const posted = await trpc.purchasing.postReceipt.mutate({ id: saved.id });
        applyReceipt(posted as Receipt);
        toast.showToast(t("inventory.purchases.posted"), { type: "success" });
        props.onClose(true);
      }, "inventory.purchases.post_failed");

    const renderHeader = () => (
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label class="block">
          <span class="mb-1 block text-sm font-medium">{t("inventory.purchases.supplier")}</span>
          <select
            class="select select-bordered select-sm w-full"
            disabled={readOnly.value}
            value={supplierId.value ?? ""}
            onChange={(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              supplierId.value = v ? Number(v) : null;
            }}
          >
            <option value="">{t("inventory.purchases.no_supplier")}</option>
            {supplierList.value.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label class="block">
          <span class="mb-1 block text-sm font-medium">{t("inventory.purchases.reference")}</span>
          <input
            class="input input-bordered input-sm w-full"
            disabled={readOnly.value}
            placeholder={t("inventory.purchases.reference_placeholder")}
            v-model={reference.value}
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-sm font-medium">{t("inventory.purchases.received_on")}</span>
          <input
            type="date"
            class="input input-bordered input-sm w-full"
            disabled={readOnly.value}
            v-model={receivedOn.value}
          />
        </label>
      </div>
    );

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        );
      }
      if (stockItems.value.length === 0) {
        return <div class="p-6 text-sm text-base-content/70">{t("inventory.recipe.no_stock_items")}</div>;
      }
      return (
        <div class="space-y-4 p-6">
          {imported.value && !readOnly.value && (
            <div class={["alert py-2 text-sm", imported.value.unmatched ? "alert-warning" : "alert-info"].join(" ")}>
              {(imported.value.scan
                ? imported.value.unmatched
                  ? t("inventory.purchases.scanned")
                  : t("inventory.purchases.scanned_all_matched")
                : imported.value.unmatched
                  ? t("inventory.purchases.imported")
                  : t("inventory.purchases.imported_all_matched")
              )
                .replace("{total}", String(imported.value.total))
                .replace("{unmatched}", String(imported.value.unmatched))}
            </div>
          )}
          {renderHeader()}
          <div class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{t("inventory.recipe.col_item")}</th>
                  <th>{t("inventory.recipe.col_qty")}</th>
                  <th>{t("inventory.recipe.col_unit")}</th>
                  <th>{t("inventory.purchases.col_unit_price")}</th>
                  <th class="text-right">{t("inventory.purchases.col_total")}</th>
                  <th class="text-right">{t("inventory.purchases.col_change")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.value.map((line, index) => (
                  <ReceiptLineRow
                    key={index}
                    line={line}
                    stockItems={stockItems.value}
                    readOnly={readOnly.value}
                    onChange={(next: EditableReceiptLine) =>
                      (lines.value = lines.value.map((l, i) => (i === index ? next : l)))
                    }
                    onRemove={() => (lines.value = lines.value.filter((_, i) => i !== index))}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly.value && (
            <button class="btn btn-ghost btn-sm" onClick={() => (lines.value = [...lines.value, emptyLine()])}>
              <span class="material-symbols-outlined text-base">add</span>
              {t("inventory.purchases.add_line")}
            </button>
          )}
          <div class="flex flex-wrap items-end justify-between gap-4 border-t border-base-300 pt-4">
            <label class="block w-full max-w-sm">
              <span class="mb-1 block text-sm font-medium">{t("inventory.purchases.notes")}</span>
              <textarea
                class="textarea textarea-bordered w-full text-sm"
                rows={2}
                disabled={readOnly.value}
                v-model={notes.value}
              />
            </label>
            <div class="text-right">
              <div class="text-sm text-base-content/60">{t("inventory.purchases.col_total")}</div>
              <div class="text-2xl font-semibold">{total.value.toFixed(2)}</div>
              <div class="text-xs text-base-content/50">{t("inventory.purchases.excl_vat")}</div>
            </div>
          </div>
          {documentKey.value && (
            <a
              class="link link-primary inline-flex items-center gap-1 text-sm"
              href={`/api/purchasing/documents/${encodeURIComponent(documentKey.value)}`}
              target="_blank"
              rel="noopener"
            >
              <span class="material-symbols-outlined text-base">description</span>
              {t("inventory.scan.view_document")}
            </a>
          )}
          {unmapped.value > 0 && !readOnly.value && (
            <div class="alert alert-warning py-2 text-sm">
              {t("inventory.purchases.unmapped_help").replace("{count}", String(unmapped.value))}
            </div>
          )}
          {held.value && !readOnly.value && (
            <div class="alert alert-warning py-2 text-sm">{t("inventory.purchases.held_help")}</div>
          )}
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={
          readOnly.value
            ? t("inventory.purchases.posted_title")
            : id.value
              ? t("inventory.purchases.edit_title")
              : t("inventory.purchases.new_title")
        }
        onClose={() => props.onClose(changed.value)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(changed.value)}>
                {readOnly.value ? t("inventory.purchases.close") : t("general.words.cancel")}
              </button>
              {!readOnly.value && (
                <div class="flex gap-2">
                  <button
                    class="btn btn-ghost"
                    disabled={loading.value || busy.value || invalid.value || unmapped.value > 0}
                    onClick={handleSave}
                  >
                    {t("inventory.purchases.save_draft")}
                  </button>
                  <button
                    class="btn btn-primary"
                    disabled={
                      loading.value || busy.value || invalid.value || held.value ||
                      unmapped.value > 0 || filledLines.value.length === 0
                    }
                    onClick={handlePost}
                  >
                    {t("inventory.purchases.post")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      >
        {renderBody()}
      </LemmonModal>
    );
  },
});
